import { useMemo, useState } from 'react';
import { cn } from '@/utils/helpers';
import SystemsSection from './sections/SystemsSection';
import PlansSection from './sections/PlansSection';
import AddonsSection from './sections/AddonsSection';
import LimitKeysSection from './sections/LimitKeysSection';
import AdminAccessSection from './sections/AdminAccessSection';
import FeatureFlagsSection from './sections/FeatureFlagsSection';
import AuditLogSection from './sections/AuditLogSection';
import ImpersonationPolicySection from './sections/ImpersonationPolicySection';
import BillingTextsSection from './sections/BillingTextsSection';
import BetaProgramsSection from './sections/BetaProgramsSection';
import SupportReplies from '../../command/SupportReplies';
import '../command-center.css';

const TABS = [
  { id: 'systems', label: 'Systems' },
  { id: 'plans', label: 'Plans' },
  { id: 'addons', label: 'Add-ons' },
  { id: 'limit-keys', label: 'Limit keys' },
  { id: 'billing-texts', label: 'Billing texts' },
  { id: 'admin-access', label: 'Admin access' },
  { id: 'feature-flags', label: 'Feature flags' },
  { id: 'support-replies', label: 'מענה ללקוחות' },
  { id: 'beta-programs', label: 'Beta programs' },
  { id: 'audit-log', label: 'Audit log' },
  { id: 'impersonation-policy', label: 'Impersonation policy' },
] as const;

type TabId = typeof TABS[number]['id'];

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>(() => new URLSearchParams(location.search).get('section') === 'support-replies' ? 'support-replies' : 'systems');

  const content = useMemo(() => {
    switch (activeTab) {
      case 'support-replies':
        return <section className="cc" dir="rtl"><SupportReplies settings /></section>;
      case 'systems':
        return <SystemsSection />;
      case 'plans':
        return <PlansSection />;
      case 'addons':
        return <AddonsSection />;
      case 'limit-keys':
        return <LimitKeysSection />;
      case 'billing-texts':
        return <BillingTextsSection />;
      case 'admin-access':
        return <AdminAccessSection />;
      case 'feature-flags':
        return <FeatureFlagsSection />;
      case 'beta-programs':
        return (
          <div className="space-y-6">
            <BetaProgramsSection
              programKey="time_saved"
              title="Time Saved beta testers"
              description="Organization allowlist with server-side enforcement and retained revocation history."
            />
            <BetaProgramsSection
              programKey="zoom_connect"
              title="Zoom connect"
              description="Organizations that may connect Zoom. Enroll or revoke here. Access is checked in the database, not by a secret."
            />
          </div>
        );
      case 'audit-log':
        return <AuditLogSection />;
      case 'impersonation-policy':
        return <ImpersonationPolicySection />;
      default:
        return null;
    }
  }, [activeTab]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ink-primary">Settings Hub</h1>
        <p className="mt-1 text-sm text-ink-secondary">
          Single place to configure admin-portal-owned systems, access, flags, and policy.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-navy-border pb-3">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'rounded-full px-3 py-2 text-sm transition-colors',
              activeTab === tab.id
                ? 'bg-gold text-navy font-semibold'
                : 'bg-navy-card text-ink-secondary hover:text-ink-primary border border-navy-border'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {content}
    </div>
  );
}
